<script setup>
import { watch, computed, nextTick, shallowRef } from 'vue'
import { RouterView, useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { cloneDeep } from 'lodash-es'
import { useI18n } from 'vue-i18n'

import config from '@/config'
import { useConfigsStore } from '@/stores/configs'
import { useUserStore } from '@/stores/user'
import { useUiStore } from '@/stores/ui'
import { useConfirm } from '@/composables/ui'
import { useApi } from 'soykajs/composables/api'
import { resolvePostLoginRoute } from '@/lib/defaultRoute'

import Modalstack from '@/components/modalstack.vue'
import MessageStack from '@/components/messagestack.vue'
import BlockScreen from '@/components/blockscreen.vue'
import SignedLayout from '@/components/layouts/signed.vue'

import Login from '@/views/login.vue'
import SsoLogin from '@/views/login/sso.vue'
import Fillinfo from '@/views/login/fillinfo.vue'

const { t } = useI18n()
const userStore = useUserStore()
const { isAuthorized, isValid, isLoggedIn, user } = storeToRefs(userStore)
const { returnUrl } = storeToRefs(useUiStore())

const router = useRouter()
const route = useRoute()
const isSsoLogin = computed(() => router.currentRoute.name === 'sso_login')
const query = shallowRef()

// Компоненты страниц, которые не должны пересоздаваться при переходах туда-обратно —
// см. KeepAlive в шаблоне ниже и docs/pdf-editor/implementation.md (FinderPdfEditor:
// пересоздание ломает legacy.js — переменные верхнего уровня классического скрипта
// переобъявляются при повторной вставке).
// FinderList: /docview из Досье не должен размонтировать vue-finder (пустой экран
// на несколько секунд при закрытии просмотрщика).
const KEEP_ALIVE_COMPONENTS = ['FinderPdfEditor', 'FinderList']

const { model: drafts, refresh: draftsFetch } = useApi({
  url: 'draft',
  before: () => config.VITE_DRAFT_ENABLED === 'true',
})
const { model: ssoResponse } = useApi({
  url: '/web/autologin',
  query,
  watch: [query],
  method: 'post',
})

watch(
  isLoggedIn,
  async isLoggedIn => {
    if (!isLoggedIn) {
      return
    }
    await useConfigsStore().fill()
    await nextTick()
    await draftsFetch()
  },
  { immediate: true }
)

watch(
  isLoggedIn,
  (isLoggedIn, isLoggedInPrev) => {
    if (isLoggedInPrev && !isLoggedIn) {
      router.push({ name: 'login' })
      return
    }

    if (
      !isLoggedInPrev &&
      isLoggedIn &&
      (['sso_login', 'login'].includes(router.currentRoute.value.name) ||
        (router.currentRoute.value.name === 'home' && returnUrl.value))
    ) {
      const targetRoute = resolvePostLoginRoute(userStore, returnUrl.value)
      router.push(targetRoute)
      // Очищаем returnUrl только после успешного редиректа
      returnUrl.value = undefined
    }
  },
  { flush: 'post', immediate: false }
)

watch(drafts, async drafts => {
  if (!drafts) {
    return
  }

  if (router.currentRoute.value.fullPath == drafts.reflink) {
    return
  }

  if (!(await useConfirm(t('common.draftRestorePrompt'))())) {
    await useApi({
      url: `draft/${drafts.model_name}/${drafts.model_id}`,
      method: 'DELETE',
    }).refresh()
    return
  }

  router.replace(drafts.reflink)
})

watch(
  () => route.query,
  qry => {
    if (!qry.token || !qry.service) {
      return
    }

    query.value = { token: qry.token, service: qry.service }
  },
  { immediate: true }
)

watch(ssoResponse, resp => {
  if (!resp?.access_token) {
    return
  }

  user.value = {
    ...cloneDeep(user.value),
    username: resp.username,
    access_token: resp.access_token,
  }
})
</script>

<template>
  <SsoLogin v-if="isSsoLogin" />
  <template v-else>
    <Login v-if="!isAuthorized" />
    <BlockScreen v-else-if="!isLoggedIn" />
    <template v-else>
      <SignedLayout v-if="isValid">
        <RouterView
          v-if="$route.name != 'login'"
          v-slot="{ Component }"
        >
          <!-- KeepAlive должен быть смонтирован ВСЕГДА — v-if здесь вместо
            :include уничтожает кэш при уходе с роута (проверено на практике). Сейчас
            кэшируется только FinderPdfEditor из-за его особенностей реализации
          — см. docs/pdf-editor/implementation.md. -->
          <KeepAlive :include="KEEP_ALIVE_COMPONENTS">
            <component :is="Component" />
          </KeepAlive>
        </RouterView>
        <BlockScreen v-else />
      </SignedLayout>
      <Fillinfo v-else />
    </template>
  </template>

  <Teleport
    to="body"
    v-if="isAuthorized"
  >
    <Modalstack />
  </Teleport>
  <Teleport to="body">
    <MessageStack />
  </Teleport>
</template>
